import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
    Plus, 
    Search, 
    Edit, 
    Trash2, 
    UtensilsCrossed, 
    Coffee, 
    Wine, 
    IceCream,
    Droplets,
    Calculator,
    Archive,
    FileDown,
    FileSpreadsheet,
    Download,
    Image,
    Eye,
    EyeOff,
    CheckCircle,
    XCircle,
    ChefHat,
    Settings
} from 'lucide-react';

const MenuPage = () => {
    const { api, hasPermission, user } = useAuth();
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    
    // Permissions - Directors and Assistant Directors can manage menu
    const canManageMenu = user?.role === 'ADMIN' || user?.role === 'DIRECTEUR' || user?.role === 'DIRECTEUR_ADJOINT';
    const canViewCosts = hasPermission?.('view_recipe_costs') ?? canManageMenu;

    // Menu categories
    const categories = [
        { id: 'plats', name: 'Plats', icon: UtensilsCrossed },
        { id: 'boissons', name: 'Boissons', icon: Wine },
        { id: 'cafe', name: 'Café/Thé', icon: Coffee },
        { id: 'desserts', name: 'Desserts', icon: IceCream },
        { id: 'supplements', name: 'Suppléments', icon: Plus },
        { id: 'autres', name: 'Autres', icon: Droplets },
    ];

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        category: 'plats',
        cost_price: '',
        coefficient: '2.5',
        sale_price: '',
        description: '',
        image_url: '',
        is_active: true,
        is_available: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            // Use pos-products as the single source of truth
            const res = await api().get('/pos/products');
            setMenuItems(res.data);
        } catch (error) {
            // Fallback to recipes
            try {
                const res = await api().get('/recipes');
                const converted = res.data.map(r => ({
                    ...r,
                    category: 'plats',
                    cost_price: r.cost_price_cfa || 0,
                    coefficient: 2.5,
                    sale_price: r.sale_price_cfa || 0
                }));
                setMenuItems(converted);
            } catch (e) {
                toast.error('Erreur lors du chargement');
            }
        } finally {
            setLoading(false);
        }
    };

    // Export functions
    const handleExportPDF = async () => {
        try {
            toast.loading('Génération du PDF...');
            const response = await api().get('/exports/menu/pdf', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `menu_${new Date().toISOString().split('T')[0]}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.dismiss();
            toast.success('PDF téléchargé');
        } catch (error) {
            toast.dismiss();
            toast.error('Erreur lors de la génération du PDF');
        }
    };

    const handleExportExcel = async () => {
        try {
            toast.loading('Génération du fichier Excel...');
            const response = await api().get('/exports/menu/excel', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `menu_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.dismiss();
            toast.success('Excel téléchargé');
        } catch (error) {
            toast.dismiss();
            toast.error('Erreur lors de la génération du fichier Excel');
        }
    };

    const calculateSalePrice = (cost, coef) => {
        return Math.round((parseFloat(cost) || 0) * (parseFloat(coef) || 1));
    };

    const calculateMargin = (cost, sale) => {
        const c = parseFloat(cost) || 0;
        const s = parseFloat(sale) || 0;
        if (s === 0) return 0;
        return Math.round(((s - c) / s) * 100);
    };

    const handleCostChange = (value) => {
        const newSalePrice = calculateSalePrice(value, formData.coefficient);
        setFormData({ ...formData, cost_price: value, sale_price: newSalePrice.toString() });
    };

    const handleCoefChange = (value) => {
        const newSalePrice = calculateSalePrice(formData.cost_price, value);
        setFormData({ ...formData, coefficient: value, sale_price: newSalePrice.toString() });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.error('Le nom est obligatoire');
            return;
        }

        try {
            const payload = {
                name: formData.name,
                category: formData.category,
                cost_price: parseFloat(formData.cost_price) || 0,
                coefficient: parseFloat(formData.coefficient) || 2.5,
                sale_price: parseFloat(formData.sale_price) || 0,
                description: formData.description,
                image_url: formData.image_url || null,
                is_active: formData.is_active,
                is_available: formData.is_available
            };

            if (editingItem) {
                await api().put('/pos/products/' + editingItem.id, payload);
                toast.success('Article mis à jour');
            } else {
                await api().post('/pos/products', payload);
                toast.success('Article créé');
            }

            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            name: item.name,
            category: item.category || 'plats',
            cost_price: (item.cost_price || item.cost_price_cfa || '').toString(),
            coefficient: (item.coefficient || '2.5').toString(),
            sale_price: (item.sale_price || item.sale_price_cfa || '').toString(),
            description: item.description || '',
            image_url: item.image_url || '',
            is_active: item.is_active !== false,
            is_available: item.is_available !== false
        });
        setDialogOpen(true);
    };

    const handleArchive = async (item) => {
        if (!window.confirm('Archiver "' + item.name + '" ?')) return;
        try {
            await api().put('/pos/products/' + item.id, { ...item, is_active: false });
            toast.success('Article archivé');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDelete = async (itemId) => {
        if (!window.confirm('Supprimer définitivement ?')) return;
        try {
            await api().delete('/pos/products/' + itemId);
            toast.success('Article supprimé');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    // Toggle disponibilité rapidement
    const toggleAvailability = async (item) => {
        try {
            const newAvailability = !(item.is_available !== false);
            await api().put('/pos/products/' + item.id, { 
                ...item, 
                is_available: newAvailability 
            });
            toast.success(newAvailability ? `${item.name} est maintenant disponible` : `${item.name} n'est plus disponible`);
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const resetForm = () => {
        setEditingItem(null);
        setFormData({
            name: '',
            category: 'plats',
            cost_price: '',
            coefficient: '2.5',
            sale_price: '',
            description: '',
            image_url: '',
            is_active: true,
            is_available: true
        });
    };

    const getCategoryName = (catId) => {
        const cat = categories.find(c => c.id === catId);
        return cat ? cat.name : 'Plats';
    };

    const filteredItems = menuItems.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = activeCategory === 'all' || (item.category || 'plats') === activeCategory;
        const isActive = item.is_active !== false;
        return matchesSearch && matchesCategory && isActive;
    });

    const getCategoryCount = (catId) => {
        return menuItems.filter(item => 
            (item.category || 'plats') === catId && item.is_active !== false
        ).length;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="menu-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground dark:text-white">Menu & Tarifs</h1>
                    <p className="text-muted-foreground">Gestion des plats, boissons, cafés et desserts</p>
                </div>
                <div className="flex gap-2">
                    {/* Gestion des Produits */}
                    {canManageMenu && (
                        <Link to="/menu-products">
                            <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                                <ChefHat className="w-4 h-4 mr-2" />
                                Gérer les Produits
                            </Button>
                        </Link>
                    )}
                    
                    {/* Export Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" data-testid="export-menu-btn">
                                <Download className="w-4 h-4 mr-2" />
                                Exporter
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
                                <FileDown className="w-4 h-4 mr-2 text-red-500" />
                                Exporter en PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-btn">
                                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                                Exporter en Excel
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    
                    {canManageMenu && (
                        <Button 
                            onClick={() => { resetForm(); setDialogOpen(true); }}
                            data-testid="add-menu-item-btn"
                            className="bg-green-600 hover:bg-green-700"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvel Article
                        </Button>
                    )}
                </div>
            </div>

            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Rechercher..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Select value={activeCategory} onValueChange={setActiveCategory}>
                            <SelectTrigger className="w-full sm:w-48">
                                <SelectValue placeholder="Catégorie" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous ({menuItems.filter(i => i.is_active !== false).length})</SelectItem>
                                {categories.map(cat => (
                                    <SelectItem key={cat.id} value={cat.id}>
                                        {cat.name} ({getCategoryCount(cat.id)})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <UtensilsCrossed className="w-5 h-5" />
                        Articles du Menu
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Article</TableHead>
                                <TableHead>Catégorie</TableHead>
                                <TableHead>Dispo</TableHead>
                                {canViewCosts && <TableHead>Coût MP</TableHead>}
                                {canViewCosts && <TableHead>Coef.</TableHead>}
                                <TableHead>Prix Vente</TableHead>
                                {canViewCosts && <TableHead>Marge</TableHead>}
                                {canManageMenu && <TableHead className="text-right">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                                        Aucun article trouvé
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredItems.map(item => {
                                    const cost = item.cost_price || item.cost_price_cfa || 0;
                                    const sale = item.sale_price || item.sale_price_cfa || 0;
                                    const coef = item.coefficient || (sale && cost ? (sale / cost).toFixed(1) : '-');
                                    const margin = calculateMargin(cost, sale);
                                    const isAvailable = item.is_available !== false;
                                    
                                    return (
                                        <TableRow key={item.id} className={!isAvailable ? 'opacity-50' : ''}>
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    {item.image_url && (
                                                        <img 
                                                            src={item.image_url} 
                                                            alt={item.name}
                                                            className="w-8 h-8 rounded object-cover"
                                                            onError={(e) => e.target.style.display = 'none'}
                                                        />
                                                    )}
                                                    <span>{item.name}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{getCategoryName(item.category)}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <button
                                                    onClick={() => canManageMenu && toggleAvailability(item)}
                                                    className={`cursor-pointer ${!canManageMenu ? 'cursor-default' : ''}`}
                                                    disabled={!canManageMenu}
                                                >
                                                    {isAvailable ? (
                                                        <Badge className="bg-green-100 text-green-700 hover:bg-green-200 transition-colors">
                                                            <Eye className="w-3 h-3 mr-1" />
                                                            Oui
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-red-100 text-red-700 hover:bg-red-200 transition-colors">
                                                            <EyeOff className="w-3 h-3 mr-1" />
                                                            Non
                                                        </Badge>
                                                    )}
                                                </button>
                                            </TableCell>
                                            {canViewCosts && (
                                                <TableCell>{cost.toLocaleString()} F</TableCell>
                                            )}
                                            {canViewCosts && (
                                                <TableCell>x{coef}</TableCell>
                                            )}
                                            <TableCell className="font-semibold">
                                                {sale.toLocaleString()} FCFA
                                            </TableCell>
                                            {canViewCosts && (
                                                <TableCell>
                                                    <Badge className={margin >= 50 ? 'bg-green-500' : margin >= 30 ? 'bg-orange-500' : 'bg-red-500'}>
                                                        {margin}%
                                                    </Badge>
                                                </TableCell>
                                            )}
                                            {canManageMenu && (
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => handleArchive(item)} className="text-orange-600">
                                                            <Archive className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-600">
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingItem ? 'Modifier l\'article' : 'Nouvel article'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nom *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Poulet braisé, Café expresso..."
                                required
                                data-testid="menu-item-name"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Catégorie *</Label>
                            <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                                <SelectTrigger data-testid="menu-item-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <Calculator className="w-4 h-4" />
                                Calcul du prix
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Coût matières (FCFA)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={formData.cost_price}
                                        onChange={(e) => handleCostChange(e.target.value)}
                                        data-testid="menu-item-cost"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Coefficient (x)</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        step="0.1"
                                        value={formData.coefficient}
                                        onChange={(e) => handleCoefChange(e.target.value)}
                                        data-testid="menu-item-coefficient"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Prix de vente (FCFA)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.sale_price}
                                    onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                                    className="text-lg font-semibold"
                                    data-testid="menu-item-price"
                                />
                                {formData.cost_price && formData.sale_price && (
                                    <p className="text-sm text-gray-500">
                                        Marge: {calculateMargin(formData.cost_price, formData.sale_price)}%
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Description du plat pour le client..."
                                rows={3}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Image className="w-4 h-4" />
                                URL de l'image
                            </Label>
                            <Input
                                value={formData.image_url}
                                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                placeholder="https://exemple.com/image.jpg"
                            />
                            {formData.image_url && (
                                <div className="relative w-24 h-24 rounded-lg overflow-hidden border">
                                    <img 
                                        src={formData.image_url} 
                                        alt="Aperçu" 
                                        className="w-full h-full object-cover"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                                {formData.is_available ? (
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                    <XCircle className="w-5 h-5 text-red-500" />
                                )}
                                <div>
                                    <Label className="font-medium">Disponible à la vente</Label>
                                    <p className="text-sm text-gray-500">
                                        {formData.is_available 
                                            ? 'Ce plat est visible et commandable' 
                                            : 'Ce plat n\'est pas disponible actuellement'}
                                    </p>
                                </div>
                            </div>
                            <Switch
                                checked={formData.is_available}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" data-testid="submit-menu-item">
                                {editingItem ? 'Mettre à jour' : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MenuPage;
